import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw } from "lucide-react";
import { BodyFigure } from "./BodyFigure";
import { MODES, REGIONS } from "./chainData";
import { POSTURE } from "./chainPosture";

function useStepper(length, resetKey, ms) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    setStep(0);
    const t = setInterval(() => setStep((s) => (s < length - 1 ? s + 1 : s)), ms);
    return () => clearInterval(t);
  }, [length, resetKey, ms]);
  return Math.min(step, length - 1);
}

function ModeToggle({ mode, setMode, suffix = "" }) {
  return (
    <div data-testid={`chain-mode-toggle${suffix}`} className="inline-flex rounded-full border border-espresso/15 bg-ivory p-1">
      {Object.entries(MODES).map(([key, m]) => (
        <button key={key} onClick={() => setMode(key)} data-testid={`chain-mode-${key}${suffix}`}
          className={`whitespace-nowrap rounded-full px-3 sm:px-5 py-2 text-xs uppercase tracking-[0.16em] transition-colors duration-300 ${mode === key ? "bg-espresso text-ivory" : "text-espresso hover:bg-linen"}`}>
          {m.label}
        </button>
      ))}
    </div>
  );
}

function Block({ label, children, testid }) {
  return (
    <div className="mt-7" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.18em] text-taupe mb-2">{label}</div>
      <p className="font-light leading-relaxed text-espressosoft/85">{children}</p>
    </div>
  );
}

function Cascade({ id, step }) {
  return (
    <div className="mt-8" data-testid="chain-cascade">
      <div className="text-xs uppercase tracking-[0.18em] text-taupe mb-3">Chain reaction</div>
      <ol className="border-l border-espresso/15">
        {POSTURE[id].cascade.map((c, i) => (
          <li key={c.text} data-testid={`chain-cascade-step-${i}`}
            className={`relative pl-6 py-2.5 transition-opacity duration-500 ${i < step ? "opacity-100" : "opacity-35"}`}>
            <span className={`absolute -left-[5px] top-[18px] h-2.5 w-2.5 rounded-full transition-colors duration-500 ${i === step - 1 ? "bg-brass ring-4 ring-brass/25" : i < step ? "bg-brass" : "bg-espresso/25"}`} />
            <span className="block text-sm font-medium text-espresso">{REGIONS[c.region].name}</span>
            <span className="block text-[0.95rem] font-light leading-relaxed text-espressosoft/85">{c.text}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function RegionPanel({ id, mode, step, onSelect }) {
  const r = REGIONS[id];
  const explore = mode === "explore";
  return (
    <motion.div key={`${mode}-${id}`} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.45 }} data-testid="chain-region-panel">
      <div className="overline mb-3">{r.role}</div>
      <h3 className="font-serif font-light text-4xl lg:text-5xl text-espresso" data-testid="chain-region-name">{r.name}</h3>
      <p className="mt-5 text-lg font-light leading-relaxed text-espressosoft/85 max-w-xl">{explore ? r.note : r[MODES[mode].key]}</p>
      {explore && (
        <div className="max-w-xl">
          <Block label="How it shapes posture" testid="chain-posture">{POSTURE[id].posture}</Block>
          <Block label="Where it can show up as pain" testid="chain-pain">{POSTURE[id].pain}</Block>
          <Cascade id={id} step={step} />
          <div className="mt-8 text-xs uppercase tracking-[0.18em] text-taupe mb-3">Connected to</div>
          <div className="flex flex-wrap gap-2">
            {r.links.map((l) => (
              <button key={l} onClick={() => onSelect(l)} data-testid={`chain-link-chip-${l}`}
                className="rounded-full border border-brass/50 bg-ivory px-4 py-2 text-sm text-espresso hover:bg-espresso hover:text-ivory transition-colors duration-300">
                {REGIONS[l].name}
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}

export function ChainExplorer() {
  const [mode, setMode] = useState("explore");
  const [selected, setSelected] = useState("pelvis");
  const [runId, setRunId] = useState(0);
  const explore = mode === "explore";
  const order = explore ? [selected, ...POSTURE[selected].cascade.map((c) => c.region)] : MODES[mode].order;
  const step = useStepper(order.length, `${mode}-${selected}-${runId}`, explore ? 1500 : 900);

  const select = (id) => { setMode("explore"); setSelected(id); };
  const shown = order.slice(0, step + 1);
  const current = explore ? shown[shown.length - 1] : order[step];
  const pairs = shown.slice(1).map((id, i) => [shown[i], id]).filter(([a, b]) => a !== b);
  const label = explore ? `Chain reaction ${step} of ${order.length - 1}` : `Step ${step + 1} of ${order.length}`;

  return (
    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start" data-testid="chain-explorer">
      <div className="lg:hidden"><ModeToggle mode={mode} setMode={setMode} suffix="-mobile" /></div>
      <div className="lg:col-span-5 lg:sticky lg:top-28 bg-ivory border border-espresso/10 rounded-sm p-6 sm:p-10 flex justify-center">
        <div className="w-full max-w-[220px] sm:max-w-[300px]">
          <BodyFigure lit={[...new Set(shown)]} active={current} pairs={pairs} spine={!explore} onSelect={select} />
        </div>
      </div>
      <div className="lg:col-span-7">
        <div className="hidden lg:block"><ModeToggle mode={mode} setMode={setMode} /></div>
        <div className="lg:mt-8 mb-10 max-w-xl">
          <div className="font-serif italic text-2xl text-taupe" data-testid="chain-mode-title">{MODES[mode].title}</div>
          <p className="mt-2 text-espressosoft/75 font-light leading-relaxed">{MODES[mode].body}</p>
          <button onClick={() => setRunId((n) => n + 1)} data-testid="chain-replay" className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-espresso link-underline">
            <RotateCcw size={13} /> Replay · {label}
          </button>
        </div>
        <AnimatePresence mode="wait"><RegionPanel id={explore ? selected : current} mode={mode} step={step} onSelect={select} /></AnimatePresence>
      </div>
    </div>
  );
}
